import { ChartDataset } from "chart.js";
import classNames from "classnames";
import * as Turbo from "@hotwired/turbo";

import { ForwardedChartHandle } from "~/components/shared/Chart/chart";
import { useHighlightDataset, useToggleDatasets } from "~/components/ClustersInsights/Chart/hooks";
import { LegendIndicator } from "~/components/ClustersInsights/LegendIndicator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ClustersInsights/Table";
import Icon from "~/components/shared/Icon";

export function ChartLegend<Dataset extends ChartDataset<"line", unknown> & { url?: string }>({
  labelHeader = "Metric",
  chartRef,
  datasets,
  reverse,
  columns = {},
}: {
  labelHeader?: string;
  chartRef: React.RefObject<ForwardedChartHandle>;
  datasets: Dataset[];
  reverse?: boolean;
  columns?: Record<string, (dataset: Dataset, index: number, visible: boolean) => React.ReactNode>;
}) {
  const { isDatasetVisible, visibilityHandlers } = useToggleDatasets(chartRef);
  const highlightHandlers = useHighlightDataset(chartRef);

  const indexedDatasets: [number, Dataset][] = datasets.map((dataset, index) => [index, dataset]);

  if (reverse) {
    indexedDatasets.reverse();
  }

  return (
    <Table className="lg:basis-96">
      <TableHead>
        <TableRow>
          <TableHeader>{labelHeader}</TableHeader>
          {Object.keys(columns).map((column) => (
            <TableHeader key={column}>{column}</TableHeader>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {indexedDatasets.map(([index, dataset]) => {
          const { label, borderColor } = dataset;
          const visible = isDatasetVisible(index);

          return (
            <TableRow
              key={label}
              className={classNames("group", { "text-slate-500": !visible })}
              {...visibilityHandlers(index)}
              {...highlightHandlers(index)}
            >
              <TableCell className="whitespace-normal font-semibold" title={label}>
                <div className="line-clamp-1 flex items-baseline gap-1.5">
                  <LegendIndicator
                    color={borderColor as string}
                    className="shrink-0"
                    selected={visible}
                  />
                  <span>
                    {label}

                    {dataset.url && (
                      <a
                        className="text-charcoal-300 hover:text-charcoal-900 invisible ml-1.5 inline-block cursor-pointer bg-inherit group-hover:visible"
                        href={dataset.url}
                        data-turbo="true"
                        onClick={(event) => {
                          // Prevent item from being checked/unchecked
                          event.stopPropagation();

                          // Now manually trigger Turbo because the event was stopped
                          event.preventDefault();
                          Turbo.visit(dataset.url!);
                        }}
                      >
                        <Icon
                          icon="heroicons/outline/arrow-up-right"
                          className="inline-block h-3 w-3"
                        />
                      </a>
                    )}
                  </span>
                </div>
              </TableCell>

              {Object.keys(columns).map((column) => (
                <TableCell key={column}>{columns[column](dataset, index, visible)}</TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
